import React, { useState } from 'react';
import DatenLaden from './travelbingo/DatenLaden';
import TicketsSichten from './travelbingo/TicketsSichten';
import GewinnerZiehen from './travelbingo/GewinnerZiehen';
import { BuyerEntry } from './travelbingo/dataloaders';
import './Travelbingo.css';

export interface MSForm {
    id: string;
    name: string;
    webUrl?: string;
    createdDateTime?: string;
    lastModifiedDateTime?: string;
}

export interface FormResponse {
    id: string;
    submitDateTime?: string;
    answers?: Record<string, unknown>;
    [key: string]: unknown;
}

const Travelbingo: React.FC = () => {
    const [currentStep, setCurrentStep] = useState(1);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [selectedForm, setSelectedForm] = useState<MSForm | null>(null);
    const [formResponses, setFormResponses] = useState<FormResponse[]>([]);
    const [buyerData, setBuyerData] = useState<BuyerEntry[]>([]);
    const [randomnessSeed, setRandomnessSeed] = useState<string>('');

    const handleDataLoaded = (loaded: boolean) => {
        setDataLoaded(loaded);
    };

    const handleFormSelected = (form: MSForm | null) => {
        setSelectedForm(form);
    };

    const handleFormResponsesLoaded = (responses: FormResponse[]) => {
        setFormResponses(responses);
    };

    const handleBuyerDataLoaded = (data: BuyerEntry[]) => {
        setBuyerData(data);
    };

    const handleRandomnessSeedLoaded = (seed: string) => {
        setRandomnessSeed(seed);
    };

    const steps = [
        { 
            number: 1, 
            label: 'Daten laden', 
            component: (
                <DatenLaden 
                    onDataLoaded={handleDataLoaded}
                    onFormSelected={handleFormSelected}
                    selectedForm={selectedForm}
                    onBuyerDataLoaded={handleBuyerDataLoaded}
                    onRandomnessSeedLoaded={handleRandomnessSeedLoaded}
                />
            ) 
        },
        { 
            number: 2, 
            label: 'Tickets sichten', 
            component: (
                <TicketsSichten 
                    selectedForm={selectedForm}
                    formResponses={formResponses}
                    onFormResponsesLoaded={handleFormResponsesLoaded}
                    buyerData={buyerData}
                />
            ) 
        },
        { 
            number: 3, 
            label: 'Gewinner ziehen', 
            component: (
                <GewinnerZiehen 
                    buyerData={buyerData}
                    randomnessSeed={randomnessSeed}
                />
            ) 
        }
    ];

    const handleNext = () => {
        if (currentStep < steps.length) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handlePrevious = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    // Determine if next button should be disabled
    const isNextDisabled = () => {
        if (currentStep === 1) {
            // On step 1, next is disabled until data is loaded
            return !dataLoaded;
        }
        // On other steps, next is only disabled at the last step
        return currentStep === steps.length;
    };

    return (
        <div className="travelbingo-container">
            <h1>Travelbingo - Auswertung</h1>
            
            <div className="process-view">
                <div className="step-indicators">
                    {steps.map((step) => (
                        <div
                            key={step.number}
                            className={`step-indicator ${currentStep === step.number ? 'active' : ''} ${currentStep > step.number ? 'completed' : ''}`}
                        >
                            <div className="step-number">{step.number}</div>
                            <div className="step-label">{step.label}</div>
                        </div>
                    ))}
                </div>

                <div className="step-content-container">
                    {steps.find(step => step.number === currentStep)?.component}
                </div>

                <div className="step-navigation">
                    <button 
                        className="nav-button prev-button" 
                        onClick={handlePrevious}
                        disabled={currentStep === 1}
                    >
                        Zurück
                    </button>
                    <button 
                        className="nav-button next-button" 
                        onClick={handleNext}
                        disabled={isNextDisabled()}
                    >
                        Weiter
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Travelbingo;
